import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { NullableRegisteredUserDetails } from "@models/user.model";
import { TranslateService } from "@ngx-translate/core";
import { ProductService } from "~/src/app/services/product/product.service";
import { S3UploadService } from "~/src/app/services/s3-upoad/s3-upload.service";
import { UserService } from "~/src/app/services/user/user.service";
import { emailValidator } from "~/src/app/shared/email-validator.directive";
import { nameValidator } from "~/src/app/shared/name-validator.directive";
import { appConfig } from "~/src/environments/app.config";
import { environment } from "~/src/environments/environment";
import { Location } from '@angular/common';

@Component({
    selector: "app-profile-form",
    templateUrl: "./profile-form.component.html",
    styleUrl: "./profile-form.component.scss",
    standalone: false,
})
export class ProfileFormComponent implements OnInit {
    public profileForm!: FormGroup;
    public userDetails!: NullableRegisteredUserDetails;
    public id!: number;

    constructor(
        private userService: UserService,
        private router: Router,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private s3UploadService: S3UploadService,
        private translate: TranslateService,
        private _snackBar: MatSnackBar,
        private productService: ProductService,
        private location: Location
    ) {}

    ngOnInit(): void {
        this.profileForm = this.fb.group({
            name: ["", [Validators.required, nameValidator()]],
            email: ["", [Validators.required, emailValidator()]],
            age: ["", [Validators.required]],
            userMobileNumber: ["", Validators.required],
        });
        this.route.params.subscribe((params) => {
            this.id = params["id"];
            this.getUserDetails();
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private getUserDetails(): void {
        this.userService.getUserDetails().subscribe((response) => {
            this.userDetails = response;
            this.patchValue();
        });
    }

    public patchValue(): void {
        this.profileForm.patchValue({
            name: this.userDetails?.user.name,
            email: this.userDetails?.user.email,
            age: this.userDetails?.user.age,
            userMobileNumber: this.userDetails?.user.userMobileNumber,
        });
    }

    public clickCancel(): void {
        this.router.navigate(["/account/profileDetails"]);
    }

    public async uploadFile(event: Event, fileType: string) {
        const maxImageSize = +appConfig.fileSize.profileImageSize;
        const input = event.target as HTMLInputElement;
        const partSize = 5 * 1024 * 1024;
        if (input.files && fileType === "image") {
            const file = input.files[0];
            if (input.files[0].size < maxImageSize) {
                const parts = Math.ceil(file.size / partSize);
                const partNumbers = Array.from({ length: parts }, (_, i) => i + 1);
                let filePathWithName;
                filePathWithName = `${environment.name}/profileImage/${file.name}`;
                const url = await this.s3UploadService.uploadFile(filePathWithName, partNumbers, file);
                this.userService.uploadProfileImage({ imageUrl: url }).subscribe((uploadResponse) => {
                    this.getUserDetails();
                });
            } else {
                alert(this.getTranslation("profile.invalidMessage.maximumFileSize"));
                return;
            }
        } else {
            const url = this.userDetails?.user.profileImage;
            if (url !== undefined) {
                this.productService.deleteFile(url).subscribe((removeImageFromS3Response) => {
                    if (removeImageFromS3Response) {
                        this.userService.uploadProfileImage({ imageUrl: fileType }).subscribe((response) => {
                            if (response) {
                                this.getUserDetails();
                            }
                        });
                    }
                });
            }
        }
    }

    onSubmit(): void {
        if (this.profileForm.valid) {
            const email = this.profileForm.get("email")?.value;
            this.userService.updateEmail({ email, id: this.id }).subscribe({
                next: (response) => {
                    this.router.navigate(["/account/profileDetails"]);
                },
                error: (error) => {
                    throw new Error(error.message);
                },
            });
        }
    }

    public goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            // No previous page — means open from notification
            this.router.navigate(["/home"]);
        }
    }
}
